// ./src/utils/api/StaffPayService.ts
//
// STAFF PAY (#A4.7) — paid volunteers. One read (`staffPay`: the event's pay per person, per role and in total, with
// its rounding / minimum rules) and three writes (the rules, "Mark paid", its Undo). Every write answers with the SAME
// fresh `StaffPay`, so a screen replaces its copy instead of patching one.
//
// ⚠ The API computes every amount (eventsmagic-api `src/core/staffPay.ts` — ONE implementation). A screen shows them;
// it never re-derives pay, or two places disagree about what somebody is owed. Amounts are in `currencyCode`, the
// EVENT's currency.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

/** Mirrors the API's STAFF_PAY_STATUS. */
export const STAFF_PAY_STATUS = {
  /** No rate on the shift or its role — a volunteer, not paid staff. */
  UNPAID: "unpaid",
  /** Marked a no-show: earns nothing. */
  NO_SHOW: "no_show",
  /** Not both checked in AND out yet — the scheduled shift. */
  EXPECTED: "expected",
  /** Checked in and out — the time actually worked. */
  ACTUAL: "actual",
} as const;

export type StaffPayStatus = (typeof STAFF_PAY_STATUS)[keyof typeof STAFF_PAY_STATUS];

/** Mirrors the API's bounds on the event's pay rules (minutes). */
export const STAFF_PAY_MAX_ROUNDING_MINUTES = 240;
export const STAFF_PAY_MAX_MINIMUM_MINUTES = 1440;

const TOTALS_FIELDS = `
  expected
  actual
  openPlaces
  bonus
  total
`;

const STAFF_PAY_FIELDS = `
  eventId
  currencyCode
  roundingMinutes
  minimumMinutes
  totals { ${TOTALS_FIELDS} }
  paid
  owed
  hasPaidShifts
  byRole {
    roleId
    roleName
    totals { ${TOTALS_FIELDS} }
  }
  people {
    eventGuestId
    name
    email
    phone
    totals { ${TOTALS_FIELDS} }
    paid
    owed
    shifts {
      assignmentId
      eventGuestId
      slotId
      slotName
      roleId
      roleName
      startsAt
      endsAt
      checkedInAt
      checkedOutAt
      noShow
      status
      rate
      minutes
      amount
      breakMinutes
      bonusAmount
      bonusNote
      total
      budgetPaymentId
      paid
      payable
    }
  }
`;

const STAFF_PAY_QUERY = gql`
  query StaffPay($params: StaffPayInput) {
    staffPay(params: $params) {
      code
      data { ${STAFF_PAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAFF_PAY_SETTINGS_SAVE = gql`
  mutation StaffPaySettingsSave($params: StaffPaySettingsInput) {
    staffPaySettingsSave(params: $params) {
      code
      data { ${STAFF_PAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAFF_PAY_MARK_PAID = gql`
  mutation StaffPayMarkPaid($params: StaffPayMarkPaidInput) {
    staffPayMarkPaid(params: $params) {
      code
      data { ${STAFF_PAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAFF_PAY_UNMARK_PAID = gql`
  mutation StaffPayUnmarkPaid($params: StaffPayUnmarkPaidInput) {
    staffPayUnmarkPaid(params: $params) {
      code
      data { ${STAFF_PAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export interface StaffPayTotals {
  /** Shifts not yet checked in AND out, plus the open places. */
  expected: number;
  /** Shifts checked in and out. */
  actual: number;
  /** The places nobody holds yet (part of `expected`). */
  openPlaces: number;
  bonus: number;
  /** expected + actual — what the event's "Staff pay" budget line carries. */
  total: number;
}

export interface StaffPayShift {
  assignmentId: string;
  eventGuestId?: string | null;
  slotId?: string | null;
  slotName?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  noShow?: boolean | null;
  status: StaffPayStatus | string;
  /** The hourly rate applied (the check-in snapshot once checked in); null for an unpaid shift. */
  rate?: number | null;
  /** Billable minutes: worked (or scheduled) − break, rounded to the nearest, raised to the minimum. */
  minutes?: number | null;
  amount: number;
  breakMinutes?: number | null;
  bonusAmount?: number | null;
  bonusNote?: string | null;
  /** amount + bonus. */
  total: number;
  budgetPaymentId?: string | null;
  paid?: boolean | null;
  /** ⚠ The SERVER's call: settled, worth something, not already paid. Never re-derive this. */
  payable?: boolean | null;
}

export interface StaffPayPerson {
  eventGuestId: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  totals: StaffPayTotals;
  /** What their linked budget payments add up to. */
  paid: number;
  /** actual − paid (never below zero). */
  owed: number;
  shifts: StaffPayShift[];
}

export interface StaffPayRole {
  /** Null = shifts with no role. */
  roleId?: string | null;
  roleName?: string | null;
  totals: StaffPayTotals;
}

export interface StaffPay {
  eventId: string;
  currencyCode?: string | null;
  roundingMinutes: number;
  minimumMinutes: number;
  totals: StaffPayTotals;
  paid: number;
  owed: number;
  hasPaidShifts: boolean;
  byRole: StaffPayRole[];
  people: StaffPayPerson[];
}

export interface StaffPaySettingsInput {
  eventId: string;
  /** Round worked minutes to the NEAREST multiple (0–240); 0 = no rounding. */
  roundingMinutes: number;
  /** Pay at least this many minutes per shift (0–1440); 0 = no minimum. */
  minimumMinutes: number;
}

export interface StaffPayMarkPaidInput {
  eventId: string;
  /** One person's FINISHED shifts (checked in and out, not paid yet). */
  assignmentIds: string[];
  /** YYYY-MM-DD. */
  paidAt?: string | null;
  paymentMethod?: string | null;
  note?: string | null;
}

/**
 * A shift whose pay is FINAL and not yet paid — what "Mark paid" may take.
 * ⚠ This is the SERVER's `payable`, not a rule worked out here. It used to test `status === ACTUAL`, which meant
 * a bonus on an UNPAID volunteer could never be paid: their status never becomes "actual" (there is no rate), so
 * Mark paid refused it and the money sat in "expected" forever. Owner ruling 2026-09-20: a bonus is entered after
 * the shift is worked and before payout, so a bonus-only shift settles as soon as it carries one.
 */
export const isPayableShift = (shift: StaffPayShift): boolean =>
  shift.payable === true;

export class StaffPayService {
  /** The event's staff pay (needs budget view). */
  static async get(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(STAFF_PAY_QUERY, { params: { eventId } });
  }

  /** The event's rounding + minimum minutes (needs budget edit). Field errors: roundingMinutes / minimumMinutes. */
  static async saveSettings(params: StaffPaySettingsInput): Promise<OpResult> {
    return ApiService.executeMutation(STAFF_PAY_SETTINGS_SAVE, { params });
  }

  /** ONE budget payment for one person's finished shifts (needs budget edit). Refusals are keyed `assignmentIds`. */
  static async markPaid(params: StaffPayMarkPaidInput): Promise<OpResult> {
    return ApiService.executeMutation(STAFF_PAY_MARK_PAID, { params });
  }

  /** Undo "Mark paid": the payment is removed and its shifts are owed again (needs budget edit). */
  static async unmarkPaid(eventId: string, budgetPaymentId: string): Promise<OpResult> {
    return ApiService.executeMutation(STAFF_PAY_UNMARK_PAID, { params: { eventId, budgetPaymentId } });
  }
}
